import { PropsWithChildren, useEffect, useState } from 'react';
import { createContext } from 'use-context-selector';
import { CatalogManagementProduct, CatalogManagementProductPrice } from '@interfaces/models/sell-catalog';
import useProductPriceDrop from '@hooks/product/use-product-price-drop';
import useUser from '@hooks/user/use-user';
import {
  PriceDropZoneResponse,
  RecommendationsPriceResponse,
  SimilarSoldProduct,
} from '@interfaces/models/sales/domain/SellerPrice';

export type PriceDropContextType = {
  userId: User['id'];
  // For now, the type of user (C2C or B2C) is specified because the whole logic has been made based on catalog-management API (B2C), once remove-from-sale, price-drop and cancel-my-sale features are properly split (B2C and C2C), it will be considered C2C by default
  selectedProductB2C: CatalogManagementProduct;
  selectedProductC2C: RecommendationsPriceResponse;
  productId: string;
  sellerPrice: number;
  debouncedSellerPrice: number;
  showBuyerFees: boolean;
  onDropPrice?: (x: CatalogManagementProductPrice) => void;
  recommendedPrice: number;
  recommendedPriceFormatted: string;
  isPriceRecommendationLoading: boolean;
  error: string;
  similarSoldProducts: SimilarSoldProduct[];
  setError: (x: string) => void;
  updateSellerPrice: (newSellerPrice: number) => void;
  updateDebouncedSellerPrice: (newSellerPrice: number) => void;
  priceZone: PriceDropZoneResponse['priceZone'] | undefined;
  isPriceZoneLoading: boolean;
  priceDetails: CatalogManagementProductPrice | undefined;
  isPriceDetailsLoading: boolean;
};

type PriceDropProviderProps = PropsWithChildren<{
  selectedProductB2C?: CatalogManagementProduct;
  productId?: string;
  showBuyerFees: boolean;
  onDropPrice?: (x: CatalogManagementProductPrice) => void;
  isNewVersion?: boolean;
}>;

const PriceDropContext = createContext<PriceDropContextType>({} as PriceDropContextType);

const PriceDropProvider = ({
  isNewVersion,
  productId,
  selectedProductB2C,
  onDropPrice,
  showBuyerFees,
  children,
}: PriceDropProviderProps) => {
  const { userId } = useUser();

  const [errorMessage, setErrorMessage] = useState('');
  const [sellerPrice, setSellerPrice] = useState<number>(0);
  const [debouncedSellerPrice, setDebouncedSellerPrice] = useState<number>(0);
  const [selectedProductC2C, setSelectedProductC2C] = useState<RecommendationsPriceResponse>(null);

  const productIdToUse = selectedProductB2C?.id ?? productId;
  const { usePriceRecommendation, useGetPriceDetails, usePriceZone } = useProductPriceDrop(userId, productIdToUse);
  const { data: priceRecommendation, isLoading: isPriceRecommendationLoading } = usePriceRecommendation();
  const recommendedPrice = priceRecommendation?.recommendedPrice
    ? Math.round(priceRecommendation.recommendedPrice?.cents / 100)
    : undefined;

  const { data: priceDetails, isFetching: isPriceDetailsLoading } = useGetPriceDetails(
    debouncedSellerPrice,
    !isPriceRecommendationLoading && debouncedSellerPrice > 0 && !errorMessage,
  );

  const { data: priceZone, isFetching: isPriceZoneLoading } = usePriceZone({
    recommendedPrice,
    sellerPrice: debouncedSellerPrice,
    enabled: Boolean(isNewVersion && recommendedPrice && debouncedSellerPrice && !errorMessage),
  });

  const updateSellerPrice = (newSellerPrice: number) => {
    setSellerPrice(newSellerPrice);
  };

  const updateDebouncedSellerPrice = (newSellerPrice: number) => {
    setDebouncedSellerPrice(newSellerPrice);
  };

  const setError = (x: string) => {
    setErrorMessage(x);
  };

  useEffect(() => {
    if (priceRecommendation) {
      setSelectedProductC2C(priceRecommendation);
    } else {
      setSelectedProductC2C(null);
    }
  }, [priceRecommendation]);

  const value = {
    updateDebouncedSellerPrice,
    updateSellerPrice,
    onDropPrice,
    userId,
    productId,
    selectedProductB2C,
    selectedProductC2C,
    sellerPrice,
    debouncedSellerPrice,
    showBuyerFees,
    error: errorMessage,
    setError,
    recommendedPrice,
    recommendedPriceFormatted: priceRecommendation?.recommendedPrice?.formatted ?? '',
    isPriceRecommendationLoading,
    similarSoldProducts: priceRecommendation?.similarProducts ?? [],
    priceZone,
    isPriceZoneLoading,
    priceDetails,
    isPriceDetailsLoading,
  };

  return <PriceDropContext.Provider value={value}>{children}</PriceDropContext.Provider>;
};

export { PriceDropProvider, PriceDropContext };
